import type { SemanaPublic, TareaEstadisticasResponse, TareaPublic } from '@app/shared';
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import confetti from 'canvas-confetti';
import { CalendarDays, List, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { MosaicoAstral } from '../../../components/branding';
import { Button } from '../../../components/ui/button';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { qk } from '../../../lib/queryKeys';
import { semanaApi } from '../api/semana.api';
import { tareasApi } from '../api/tareas.api';
import { Estadisticas } from '../components/Estadisticas';
import { FormularioTarea } from '../components/FormularioTarea';
import { ObjetivoSemana } from '../components/ObjetivoSemana';
import { TarjetaTarea } from '../components/TarjetaTarea';
import { VistaSemana } from '../components/VistaSemana';
import { indiceDeMes, sumarDias } from '../lib/fechas';

type Pestana = 'tareas' | 'estadisticas';
type Vista = 'lista' | 'semana';

// ciclo-tareas-deadline: la vista elegida se recuerda en el navegador. Es una
// comodidad: si el almacenamiento no está disponible, arranca en Lista.
const CLAVE_VISTA = 'tareas.vista';

function leerVista(): Vista {
  try {
    return localStorage.getItem(CLAVE_VISTA) === 'semana' ? 'semana' : 'lista';
  } catch {
    return 'lista';
  }
}

function guardarVista(vista: Vista): void {
  try {
    localStorage.setItem(CLAVE_VISTA, vista);
  } catch {
    /* localStorage no disponible */
  }
}

// ciclo-tareas, R5: sin pantalla de bienvenida — entrar a /tareas muestra el
// listado directo. Circuito planificar/ejecutar/cerrar (28/09/2026): mientras
// la semana está PLANIFICADA se puede armar la lista; al guardarla se congela
// (solo checks) hasta cerrarla, momento en que nace la semana siguiente.
export function TareasPage(): JSX.Element {
  const es = useI18n();
  const t = es.tareas;
  const queryClient = useQueryClient();
  const [pestana, setPestana] = useState<Pestana>('tareas');
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [tareaEnEdicion, setTareaEnEdicion] = useState<TareaPublic | null>(null);
  const [orden, setOrden] = useState<TareaPublic[]>([]);
  const [confirmarCerrarAbierto, setConfirmarCerrarAbierto] = useState(false);
  const [vista, setVista] = useState<Vista>(leerVista);

  function elegirVista(v: Vista): void {
    setVista(v);
    guardarVista(v);
  }

  const { data: semana } = useQuery({ queryKey: qk.tareas.semanaActual, queryFn: semanaApi.actual });
  const { data: tareas } = useQuery({ queryKey: qk.tareas.all, queryFn: tareasApi.listar });
  const { data: estadisticas } = useQuery({
    queryKey: qk.tareas.estadisticas,
    queryFn: tareasApi.estadisticas,
    enabled: pestana === 'estadisticas',
  });

  useEffect(() => {
    if (tareas) setOrden(tareas);
  }, [tareas]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const semanaGuardada = semana?.estado === 'GUARDADA';
  const puedeCrear = semana?.estado === 'PLANIFICADA';
  const meses = es.publicar.calendario.meses.split('|');
  // El mes del jueves de la semana (la regla ISO): una semana que cruza de mes
  // se cuenta en el mes que tiene más días.
  const mesDeLaSemana = (semana ? meses[indiceDeMes(sumarDias(semana.inicio, 3))] : '') ?? '';

  function invalidarTareas(): void {
    void queryClient.invalidateQueries({ queryKey: qk.tareas.all });
    void queryClient.invalidateQueries({ queryKey: qk.tareas.estadisticas });
  }

  const toggleCompletada = useMutation({
    mutationFn: (tarea: TareaPublic) => tareasApi.editar(tarea.id, { completada: !tarea.completada }),
    onSuccess: invalidarTareas,
    onError: (e) => toast.error(getErrorMessage(e, es.errores.generico.algoSalioMal)),
  });

  // ciclo-tareas-deadline, R6: arrastrar a otro día de la vista Semana. Se
  // mueve en pantalla al soltar, y si el backend rechaza, vuelve a su lugar.
  const mover = useMutation({
    mutationFn: ({ tarea, venceEl }: { tarea: TareaPublic; venceEl: string }) =>
      tareasApi.editar(tarea.id, { venceEl }),
    onMutate: ({ tarea, venceEl }) => {
      setOrden((actual) => actual.map((x) => (x.id === tarea.id ? { ...x, venceEl } : x)));
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: qk.tareas.all }),
    onError: (e) => toast.error(getErrorMessage(e, es.errores.generico.algoSalioMal)),
  });

  const toggleActiva = useMutation({
    mutationFn: (tarea: TareaPublic) => tareasApi.editar(tarea.id, { activa: !tarea.activa }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.tareas.all }),
    onError: (e) => toast.error(getErrorMessage(e, es.errores.generico.algoSalioMal)),
  });

  // D-044 (19/10/2026, pedido de Javier): eliminar directo desde la tarjeta,
  // sin abrir la edición. Pide confirmación: borrar no se deshace (el historial
  // de semanas cerradas se conserva igual).
  const [tareaAEliminar, setTareaAEliminar] = useState<TareaPublic | null>(null);
  const eliminar = useMutation({
    mutationFn: (tarea: TareaPublic) => tareasApi.eliminar(tarea.id),
    onSuccess: () => {
      invalidarTareas();
      toast.success(t.dialogo.exitoEliminada);
      setTareaAEliminar(null);
    },
    onError: (e) => toast.error(getErrorMessage(e, es.errores.generico.algoSalioMal)),
  });

  const reordenar = useMutation({
    mutationFn: (ids: string[]) => tareasApi.reordenar(ids),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.tareas.all }),
    onError: (e) => toast.error(getErrorMessage(e, es.errores.generico.algoSalioMal)),
  });

  const guardarPlanificacion = useMutation({
    mutationFn: () => semanaApi.guardar(),
    onSuccess: (s: SemanaPublic) => {
      toast.success(t.semana.exitoGuardada.replace('{n}', String(s.numero)));
      void queryClient.invalidateQueries({ queryKey: qk.tareas.semanaActual });
    },
    onError: (e) => toast.error(getErrorMessage(e, es.errores.generico.algoSalioMal)),
  });

  const cerrarSemana = useMutation({
    mutationFn: () => semanaApi.cerrar(),
    onSuccess: (nueva: SemanaPublic) => {
      if (semana) {
        toast.success(
          t.semana.exitoCerrada
            .replace('{n}', String(semana.numero))
            .replace('{siguiente}', String(nueva.numero)),
        );
      }
      setConfirmarCerrarAbierto(false);
      void queryClient.invalidateQueries({ queryKey: qk.tareas.semanaActual });
      invalidarTareas();
    },
    onError: (e) => toast.error(getErrorMessage(e, es.errores.generico.algoSalioMal)),
  });

  function abrirCrear(): void {
    setTareaEnEdicion(null);
    setDialogoAbierto(true);
  }

  function abrirEditar(tarea: TareaPublic): void {
    setTareaEnEdicion(tarea);
    setDialogoAbierto(true);
  }

  function onCompletar(tarea: TareaPublic): void {
    if (!tarea.completada) {
      void confetti({ particleCount: 120, spread: 70, origin: { y: 0.7 } });
    }
    toggleCompletada.mutate(tarea);
  }

  function onDragEnd(e: DragEndEvent): void {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setOrden((actual) => {
      const desde = actual.findIndex((t2) => t2.id === active.id);
      const hasta = actual.findIndex((t2) => t2.id === over.id);
      const siguiente = arrayMove(actual, desde, hasta);
      reordenar.mutate(siguiente.map((t2) => t2.id));
      return siguiente;
    });
  }

  return (
    // Mismo mosaico de fondo que el panel y el login, para que se sienta la
    // misma app (pedido de Javier, 28/09/2026).
    <div className="relative min-h-[calc(100vh-7.5rem)] overflow-hidden">
      <MosaicoAstral className="pointer-events-none absolute inset-0 z-0 select-none text-black/[0.07] dark:text-white/[0.09]" />
      <div
        className={cn(
          'relative z-10 mx-auto px-6 py-3',
          // Las 7 columnas de la vista Semana necesitan el ancho de la pantalla.
          pestana === 'tareas' && vista === 'semana' ? 'max-w-7xl' : 'max-w-4xl',
        )}
      >
        {semana && (
          // D-044: una sola línea liviana, sin tarjeta: el objetivo, qué semana es
          // (en chico, al lado) y la acción de la semana. Lo principal de la
          // pantalla es el calendario (Javier, 19/10/2026).
          <div className="mb-2 flex min-h-10 items-center gap-4">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <div className="min-w-0 flex-1">
                <ObjetivoSemana semana={semana} />
              </div>
              <span
                data-testid="semana-actual"
                className="shrink-0 whitespace-nowrap text-xs text-text-subtle"
              >
                {(semanaGuardada ? t.semana.enCurso : t.semana.planificando)
                  .replace('{n}', String(semana.numero))
                  .replace('{mes}', mesDeLaSemana)}
              </span>
            </div>
            {semanaGuardada ? (
              <Button
                variant="outline"
                className="shrink-0"
                onClick={() => setConfirmarCerrarAbierto(true)}
                disabled={cerrarSemana.isPending}
              >
                {cerrarSemana.isPending ? t.semana.cerrando : t.semana.cerrar}
              </Button>
            ) : (
              <Button
                className="shrink-0"
                onClick={() => guardarPlanificacion.mutate()}
                disabled={guardarPlanificacion.isPending}
              >
                {guardarPlanificacion.isPending ? t.semana.guardando : t.semana.guardar}
              </Button>
            )}
          </div>
        )}

        <div className="mb-3 flex items-center justify-between">
          <Tabs value={pestana} onChange={(v) => setPestana(v as Pestana)}>
            <TabsList>
              <TabsTrigger value="tareas">{t.pestanas.tareas}</TabsTrigger>
              <TabsTrigger value="estadisticas">{t.pestanas.estadisticas}</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-3">
            {pestana === 'tareas' && (
              <div
                role="group"
                aria-label={t.vistas.etiqueta}
                className="flex rounded-md border border-marca-neutro bg-surface p-0.5"
              >
                {(
                  [
                    ['lista', List, t.vistas.lista],
                    ['semana', CalendarDays, t.vistas.semana],
                  ] as const
                ).map(([v, Icono, etiqueta]) => (
                  <button
                    key={v}
                    type="button"
                    aria-pressed={vista === v}
                    onClick={() => elegirVista(v)}
                    className={cn(
                      'flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs font-medium transition-colors',
                      vista === v ? 'bg-marca-primario text-white' : 'text-marca-gris hover:bg-marca-neutro',
                    )}
                  >
                    <Icono size={14} />
                    {etiqueta}
                  </button>
                ))}
              </div>
            )}
            <Button size="icon" aria-label={t.crear} onClick={abrirCrear} disabled={!puedeCrear}>
              <Plus size={18} />
            </Button>
          </div>
        </div>

        {pestana === 'tareas' &&
          (orden.length === 0 ? (
            <p className="py-10 text-center text-sm text-text-subtle">{t.vacio}</p>
          ) : vista === 'semana' && semana ? (
            <VistaSemana
              tareas={orden}
              semana={semana}
              onCompletar={onCompletar}
              onEditar={abrirEditar}
              onMover={(tarea, venceEl) => mover.mutate({ tarea, venceEl })}
            />
          ) : (
            <DndContext sensors={sensors} onDragEnd={onDragEnd}>
              <SortableContext items={orden.map((t2) => t2.id)} strategy={verticalListSortingStrategy}>
                <div className="flex flex-col gap-3">
                  {orden.map((tarea) => (
                    <TarjetaTarea
                      key={tarea.id}
                      tarea={tarea}
                      semanaGuardada={semanaGuardada}
                      onToggleCompletada={() => onCompletar(tarea)}
                      onToggleActiva={() => toggleActiva.mutate(tarea)}
                      onEditar={() => abrirEditar(tarea)}
                      onEliminar={() => setTareaAEliminar(tarea)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ))}

        {pestana === 'estadisticas' && estadisticas && renderEstadisticas(estadisticas)}

        <FormularioTarea
          open={dialogoAbierto}
          onOpenChange={setDialogoAbierto}
          tarea={tareaEnEdicion}
          semana={semana}
        />

        <ConfirmDialog
          open={tareaAEliminar !== null}
          onOpenChange={(abierto) => {
            if (!abierto) setTareaAEliminar(null);
          }}
          titulo={t.dialogo.eliminar}
          descripcion={`${tareaAEliminar?.titulo ?? ''}. ${t.dialogo.confirmarEliminar}`}
          confirmar={t.dialogo.eliminar}
          confirmando={t.dialogo.eliminando}
          cancelar={t.dialogo.cancelar}
          pendiente={eliminar.isPending}
          variant="danger"
          onConfirmar={() => {
            if (tareaAEliminar) eliminar.mutate(tareaAEliminar);
          }}
        />

        {semana && (
          <ConfirmDialog
            open={confirmarCerrarAbierto}
            onOpenChange={setConfirmarCerrarAbierto}
            titulo={t.semana.cerrar}
            descripcion={t.semana.confirmarCerrar.replace('{n}', String(semana.numero))}
            confirmar={t.semana.cerrar}
            confirmando={t.semana.cerrando}
            cancelar={t.dialogo.cancelar}
            pendiente={cerrarSemana.isPending}
            onConfirmar={() => cerrarSemana.mutate()}
          />
        )}
      </div>
    </div>
  );
}

function renderEstadisticas(datos: TareaEstadisticasResponse): JSX.Element {
  return <Estadisticas datos={datos} />;
}
